import { useEffect, useMemo, useRef, useState } from 'react';
import emojiData from './reactions-300-preset.json';
import './emojiPicker.css';

export interface EmojiEntry {
  emoji: string;
  name: string;
  search: string[];
}

interface PresetEmojiEntry {
  value?: string;
  label?: string;
  keywords?: string;
  emoji?: string;
  name?: string;
  search?: string[];
}

export interface EmojiPickerButtonProps {
  onSelect: (emoji: string) => void;
  disabled?: boolean;
  title?: string;
  /** When set, skip the live catalog fetch and use this list. */
  emojis?: EmojiEntry[];
}

/** Parsed site emoji preset (~300) for pickers and the My Apps emoji keyboard. */
export const PRESET_EMOJIS: EmojiEntry[] = (emojiData as PresetEmojiEntry[])
  .map((entry) => ({
    emoji: entry.emoji ?? entry.value ?? '',
    name: entry.name ?? entry.label ?? 'Emoji',
    search:
      entry.search ??
      (entry.keywords ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
  }))
  .filter((entry) => entry.emoji);

type CatalogPage = {
  items?: Array<{ emoji?: string; name?: string; keywords?: string[] }>;
};

function mapCatalogItems(items: CatalogPage['items']): EmojiEntry[] {
  return (items ?? [])
    .map((entry) => ({
      emoji: entry.emoji ?? '',
      name: entry.name ?? 'Emoji',
      search: entry.keywords ?? []
    }))
    .filter((entry) => entry.emoji);
}

let liveCatalogPromise: Promise<EmojiEntry[] | null> | null = null;

async function fetchLiveCatalog(): Promise<EmojiEntry[] | null> {
  liveCatalogPromise ??= (async () => {
    try {
      const res = await fetch('/api/emoji-catalog?take=2000', { credentials: 'include' });
      if (!res.ok) return null;
      const page = (await res.json()) as CatalogPage;
      const mapped = mapCatalogItems(page.items);
      return mapped.length > 0 ? mapped : null;
    } catch {
      return null;
    }
  })();
  return liveCatalogPromise;
}

function matches(entry: EmojiEntry, query: string): boolean {
  if (!query) return true;
  const haystack = [entry.name, ...entry.search].join(' ').toLowerCase();
  return haystack.includes(query);
}

export function EmojiPickerButton({
  onSelect,
  disabled = false,
  title = 'Insert emoji',
  emojis
}: EmojiPickerButtonProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState<EmojiEntry[]>(emojis ?? PRESET_EMOJIS);

  useEffect(() => {
    if (emojis) {
      setCatalog(emojis);
      return;
    }
    let cancelled = false;
    void fetchLiveCatalog().then((live) => {
      if (!cancelled && live) setCatalog(live);
    });
    return () => {
      cancelled = true;
    };
  }, [emojis]);

  const normalizedQuery = query.trim().toLowerCase();
  const items = useMemo(
    () => catalog.filter((entry) => matches(entry, normalizedQuery)),
    [catalog, normalizedQuery]
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const pick = (emoji: string) => {
    onSelect(emoji);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="jarvis-emoji-picker" ref={hostRef}>
      <button
        type="button"
        className="jarvis-emoji-picker__trigger"
        title={title}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">😀</span>
      </button>
      {open ? (
        <div className="jarvis-emoji-picker__popover" role="dialog" aria-label="Emoji picker">
          <input
            ref={inputRef}
            className="jarvis-emoji-picker__search"
            value={query}
            placeholder="Search emoji..."
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="jarvis-emoji-picker__grid" role="listbox">
            {items.length === 0 ? (
              <div className="jarvis-emoji-picker__empty">No emoji found.</div>
            ) : (
              items.map((entry) => (
                <button
                  key={`${entry.emoji}-${entry.name}`}
                  type="button"
                  className="jarvis-emoji-picker__item"
                  title={entry.name}
                  aria-label={entry.name}
                  onClick={() => pick(entry.emoji)}
                >
                  {entry.emoji}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

